import React, { useMemo, useState } from 'react';
import { Heading3, Body } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';
import Dropdown, {
  type SelectedOptionOf,
} from '~/components/molecule/Dropdown';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Status from '~/components/atom/Status';
import type {
  LfaServicesV2FieldsFragment,
  GetLfaConversionDataQuery,
} from '~/graphql/types';
import type { ServicesV2Action } from '../../reducers/servicesV2Reducer';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import resolveServicesForOffice from '~/components/page/Settings/LFASettings/servicesV2/resolveServicesForOffice';
import CredentialsSection from './CredentialsSection';
import CalendarsSection from './CalendarsSection';
import AccountServicesSection from './AccountServicesSection';
import OfficeServicesView from './OfficeServicesView';

const ACCOUNT_KEY = '__account__';

const text = {
  header: 'Diensten V2',
  dropdownLabel: 'Weergave',
  accountOption: 'Account diensten (basis)',
  warningLabel: 'Waarschuwing',
};

type Props = {
  servicesV2: LfaServicesV2FieldsFragment;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  dispatch: React.Dispatch<ServicesV2Action>;
};

const ServicesV2Section: React.FC<Props> = ({
  servicesV2,
  allServices,
  dispatch,
}) => {
  const officeOptions = useOfficeOptions();
  const [selectedKey, setSelectedKey] = useState(ACCOUNT_KEY);

  const dropdownOptions = [
    {
      key: ACCOUNT_KEY,
      label: text.accountOption,
      payload: null as string | null,
    },
    ...officeOptions.map(o => ({
      key: o.key,
      label: o.label,
      payload: o.payload?.id ?? null,
    })),
  ];

  const selectedIdx = dropdownOptions.findIndex(o => o.key === selectedKey);
  const selectedOfficeId = selectedKey !== ACCOUNT_KEY ? selectedKey : null;

  const serviceNameMap = useMemo(
    () => new Map(allServices.map(s => [s.id, s.name])),
    [allServices],
  );

  const officeWarnings = useMemo(() => {
    const warnings: Array<{ officeName: string; serviceNames: Array<string> }> =
      [];

    for (const office of officeOptions) {
      const officeId = office.payload?.id;
      if (!officeId) continue;

      const resolved = resolveServicesForOffice({
        accountServices: servicesV2.accountServices,
        officeChangesets: servicesV2.officeChangesets,
        officeId,
      });

      const missing = resolved
        .filter(s => s.calendars.length === 0)
        .map(s => serviceNameMap.get(s.id) ?? s.id);

      if (missing.length > 0) {
        warnings.push({
          officeName: String(office.label),
          serviceNames: missing,
        });
      }
    }

    return warnings;
  }, [
    officeOptions,
    servicesV2.accountServices,
    servicesV2.officeChangesets,
    serviceNameMap,
  ]);

  return (
    <JustificationContainer direction="column" width="100%" gap="l">
      <Heading3 size="l" margin={[null]}>
        {text.header}
      </Heading3>

      {officeWarnings.length > 0 && (
        <JustificationContainer direction="column" gap="xs" width="100%">
          {officeWarnings.map(w => (
            <JustificationContainer key={w.officeName} align="center" gap="s">
              <Status
                label={text.warningLabel}
                textColor={{ group: 'warning' }}
                backgroundColor={{ group: 'warning', variant: 'translucent' }}
              />
              <Body size="s" margin={[null]}>
                {w.officeName}:&nbsp;
                {w.serviceNames.join(', ')}&nbsp;zonder agenda
              </Body>
            </JustificationContainer>
          ))}
        </JustificationContainer>
      )}

      <CredentialsSection
        credentials={servicesV2.credentials}
        dispatch={dispatch}
      />

      <Divider margin={['s', null]} />

      <CalendarsSection
        calendars={servicesV2.calendars}
        credentials={servicesV2.credentials}
        dispatch={dispatch}
        selectedOfficeId={selectedOfficeId}
      />

      <Divider margin={['s', null]} />

      <Dropdown
        label={text.dropdownLabel}
        options={dropdownOptions}
        selectedOptionIdx={selectedIdx}
        onChange={({ option }: SelectedOptionOf<string | null>) =>
          setSelectedKey(String(option.key))
        }
      />

      {selectedOfficeId === null ? (
        <AccountServicesSection
          servicesV2={servicesV2}
          allServices={allServices}
          dispatch={dispatch}
        />
      ) : (
        <OfficeServicesView
          officeId={selectedOfficeId}
          servicesV2={servicesV2}
          allServices={allServices}
          dispatch={dispatch}
        />
      )}
    </JustificationContainer>
  );
};

export default ServicesV2Section;
